import { cn, FOCUS_RING, Skeleton, Tabs } from "@sportsmet/ui";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import type { typePlayerTabKey } from "../../routeState/playerSearch";
import { SectionEmptyState } from "../../shell/SectionScreen";
import {
  PLAYER_GAME_LOG_NOT_STARTED_BODY,
  PLAYER_GAME_LOG_NOT_STARTED_HEADLINE,
  PLAYER_INFO_EMPTY_BODY,
  PLAYER_INFO_EMPTY_HEADLINE,
  PLAYER_INFO_LOADING_LABEL,
  PLAYER_STATS_NOT_STARTED_BODY,
  PLAYER_STATS_NOT_STARTED_HEADLINE,
  PLAYER_TABS_LABEL,
  PLAYER_UNAVAILABLE_BODY,
  PLAYER_UNAVAILABLE_HEADLINE,
} from "./playerProfileCopy";
import { PlayerGameLogTable } from "./PlayerGameLogTable";
import { PlayerProfileHeader } from "./PlayerProfileHeader";
import { PlayerStatsPanel } from "./PlayerStatsPanel";
import {
  mergePlayerProfileView,
  type typePlayerProfileDetails,
  type typePlayerProfileView,
} from "./playerProfileReader";
import { buildPlayerInfoRows, PLAYER_TAB_LABELS } from "./playerProfileView";
import {
  EMPTY_PLAYER_SEASON_VIEW,
  resolvePlayerOfferedTabs,
  type typePlayerSeasonView,
} from "./playerSeasonView";

export type typePlayerProfilePaneProps = {
  /** Null when the caller named no player. */
  view: typePlayerProfileView | null;
  /** The server reads (usePlayerProfileData). EMPTY_PLAYER_PROFILE_DETAILS when there are none. */
  details: typePlayerProfileDetails;
  tab: typePlayerTabKey;
  onSelectTab: (tab: typePlayerTabKey) => void;
  /** The season reads (usePlayerSeasonData). EMPTY_PLAYER_SEASON_VIEW, both tabs hidden, when absent. */
  season?: typePlayerSeasonView | undefined;
};

/** One fact on the Info tab (spec 9): the value in `bodyStrong` tabular over
 *  its label in `small` `textSecondary`. */
function InfoFact(props: {
  testId: string;
  label: string;
  value: ReactNode;
}): ReactNode {
  return (
    <div data-testid={props.testId} className="flex min-w-0 flex-col">
      <span className="text-body-strong text-ink tabular-nums">
        {props.value}
      </span>
      <span className="text-small text-ink-secondary">{props.label}</span>
    </div>
  );
}

export const PlayerProfilePane = (
  props: typePlayerProfilePaneProps,
): ReactNode => {
  if (props.view === null) {
    return (
      <div data-testid="player-unavailable">
        <SectionEmptyState
          headline={PLAYER_UNAVAILABLE_HEADLINE}
          body={PLAYER_UNAVAILABLE_BODY}
        />
      </div>
    );
  }

  const shown = mergePlayerProfileView({
    view: props.view,
    details: props.details,
  });
  const teamId = shown.teamId;
  const rows = buildPlayerInfoRows({
    view: shown,
    record: props.details.record,
    hasInjuryReport: props.details.hasInjuryReport,
  });

  const infoContent = (
    <div data-testid="player-info" className="flex flex-col gap-4">
      {rows.length > 0 ? (
        <div className="flex flex-wrap gap-x-6 gap-y-4">
          {rows.map((row) => (
            <InfoFact
              key={row.key}
              testId={`player-info-${row.key}`}
              label={row.label}
              value={
                row.key === "team" && teamId !== null ? (
                  <Link
                    to="/scores"
                    search={{ teamId }}
                    data-testid="player-info-team-link"
                    className={cn(
                      "inline-flex min-h-touch-target items-center text-primary no-underline",
                      FOCUS_RING,
                    )}
                  >
                    {row.value}
                  </Link>
                ) : (
                  row.value
                )
              }
            />
          ))}
        </div>
      ) : null}
      {props.details.isLoading ? (
        <div data-testid="player-info-loading">
          <Skeleton shape="text" lines={3} label={PLAYER_INFO_LOADING_LABEL} />
        </div>
      ) : null}
      {rows.length === 0 && !props.details.isLoading ? (
        <div data-testid="player-info-empty">
          <SectionEmptyState
            headline={PLAYER_INFO_EMPTY_HEADLINE}
            body={PLAYER_INFO_EMPTY_BODY}
          />
        </div>
      ) : null}
    </div>
  );

  const season = props.season ?? EMPTY_PLAYER_SEASON_VIEW;
  const offered = resolvePlayerOfferedTabs(season);
  const activeTab: typePlayerTabKey = offered.includes(props.tab)
    ? props.tab
    : (offered[0] ?? "info");
  const contentFor = (key: typePlayerTabKey): ReactNode => {
    switch (key) {
      case "info":
        return infoContent;
      case "stats":
        if (season.stats.kind === "ready") {
          return <PlayerStatsPanel groups={season.stats.groups} />;
        }
        if (season.stats.kind === "notStarted") {
          return (
            <div data-testid="player-stats-not-started">
              <SectionEmptyState
                headline={PLAYER_STATS_NOT_STARTED_HEADLINE}
                body={PLAYER_STATS_NOT_STARTED_BODY}
              />
            </div>
          );
        }
        return null;
      case "gameLog":
        if (season.gameLog.kind === "ready") {
          const { games, tables } = season.gameLog;
          return (
            <div data-testid="player-game-log" className="flex flex-col gap-4">
              {tables.map((table) => (
                <PlayerGameLogTable
                  key={table.key}
                  table={table}
                  games={games}
                />
              ))}
            </div>
          );
        }
        if (season.gameLog.kind === "notStarted") {
          return (
            <div data-testid="player-game-log-not-started">
              <SectionEmptyState
                headline={PLAYER_GAME_LOG_NOT_STARTED_HEADLINE}
                body={PLAYER_GAME_LOG_NOT_STARTED_BODY}
              />
            </div>
          );
        }
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <PlayerProfileHeader
        view={shown}
        headshotUrl={props.details.record?.headshotUrl ?? null}
      />
      {offered.length > 1 ? (
        <Tabs
          label={PLAYER_TABS_LABEL}
          variant="detail"
          value={activeTab}
          onValueChange={(value) => {
            props.onSelectTab(value as typePlayerTabKey);
          }}
          items={offered.map((key) => ({
            value: key,
            label: PLAYER_TAB_LABELS[key],
            content: contentFor(key),
          }))}
        />
      ) : (
        contentFor(activeTab)
      )}
    </div>
  );
};
